import AccessTime from '@mui/icons-material/AccessTime'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import LocalHospital from '@mui/icons-material/LocalHospital'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import People from '@mui/icons-material/People'
import Refresh from '@mui/icons-material/Refresh'
import { alpha } from '@mui/material/styles'
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import {
  checkIntoRoom,
  peekIntoRoom,
  refreshCheckIntoRoom,
} from 'src/clinic/components/CheckIn/services'
import {
  broadcastCheckInUpdate,
  updateCheckInWithValidation,
} from 'src/clinic/components/CheckIn/checkInSyncService'
import {
  getEventTypesFromCheckInOption,
  getEventTypesFromRoomData,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import { allAvailableEventTypes } from 'src/consumer/constants'
import ChamberRoomStatusIndicator from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/ChamberRoomStatusIndicator'
import CheckinRequestPromptDialog from 'src/mentor/components/CheckinRequestPromptDialog'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import {
  createEmrCheckInRequest,
  getCheckInRequestErrorMessage,
} from 'src/mentor/services/checkInRequestService'
import { EMPTY_ROSTER_ASSIGNMENT_INDEX } from '../rosterAssignments'

const IN_PERSON_CHECKIN_METHOD = 'check-in-method-in-person'
const REMOTE_CHECKIN_METHOD = 'check-in-method-remote'

const ROOM_SERVICE_CONFIG = [
  {
    key: 'consultation',
    label: 'Consultation',
    roleFlag: 'isDoctor',
    eventTypes: [allAvailableEventTypes.consultation],
  },
  {
    key: 'therapy',
    label: 'Therapy',
    roleFlag: 'isTherapist',
    eventTypes: [allAvailableEventTypes.therapySession, allAvailableEventTypes.assessment],
  },
  {
    key: 'screening',
    label: 'Screening',
    roleFlag: 'isScreener',
    eventTypes: [allAvailableEventTypes.screening],
  },
]

const ROOM_SERVICE_CONFIG_MAP = ROOM_SERVICE_CONFIG.reduce((accumulator, serviceConfig) => {
  accumulator[serviceConfig.key] = serviceConfig
  return accumulator
}, {})

function getPractitionerServicesFromAuthorities() {
  const allRoles = getAllRolesInBoolean()
  return ROOM_SERVICE_CONFIG.filter((serviceConfig) => allRoles?.[serviceConfig.roleFlag])
}

function getEventTypeIdentity(eventType) {
  return (
    eventType?.uid ||
    eventType?.id ||
    `${eventType?.type || 'unknown'}-${eventType?.contextId || 'global'}-${
      eventType?.title || eventType?.name || 'event'
    }`
  )
}

function dedupeEventTypes(eventTypes = []) {
  const seenEventTypeIds = new Set()

  return eventTypes.filter((eventType) => {
    if (!eventType) return false

    const identity = getEventTypeIdentity(eventType)
    if (seenEventTypeIds.has(identity)) return false

    seenEventTypeIds.add(identity)
    return true
  })
}

function getEnabledClinicEventTypes(mentorEventTypes = [], clinicId) {
  return mentorEventTypes.filter(
    (eventType) =>
      eventType?.enabled !== false && (!clinicId || !eventType?.contextId || eventType?.contextId === clinicId),
  )
}

function getRoomServiceKeysFromEventTypes(eventTypes = []) {
  const roomServiceKeys = new Set()

  eventTypes.forEach((eventType) => {
    const eventTypeValue = typeof eventType === 'string' ? eventType : eventType?.type

    ROOM_SERVICE_CONFIG.forEach((serviceConfig) => {
      if (serviceConfig.eventTypes.includes(eventTypeValue)) {
        roomServiceKeys.add(serviceConfig.key)
      }
    })
  })

  return [...roomServiceKeys]
}

function filterEventTypesForService(eventTypes = [], serviceKey) {
  const allowedEventTypes = ROOM_SERVICE_CONFIG_MAP?.[serviceKey]?.eventTypes || []

  return dedupeEventTypes(
    eventTypes.filter((eventType) => allowedEventTypes.includes(eventType?.type)),
  )
}

function getCheckInOptionSearchText(checkInOption) {
  return [checkInOption?.title, checkInOption?.name, checkInOption?.id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function getRoomServiceKeysFromOptionText(checkInOption) {
  const optionSearchText = getCheckInOptionSearchText(checkInOption)
  const roomServiceKeys = new Set()

  if (optionSearchText.includes('consult')) {
    roomServiceKeys.add('consultation')
  }
  if (optionSearchText.includes('therapy') || optionSearchText.includes('assessment')) {
    roomServiceKeys.add('therapy')
  }
  if (optionSearchText.includes('screen')) {
    roomServiceKeys.add('screening')
  }

  return [...roomServiceKeys]
}

function getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId) {
  const matchedEventTypes = getEventTypesFromCheckInOption(checkInOption, mentorEventTypes, clinicId)
  const directEventTypes = Array.isArray(checkInOption?.eventTypes)
    ? checkInOption.eventTypes.filter(
        (eventType) =>
          eventType?.enabled !== false &&
          (!clinicId || !eventType?.contextId || eventType?.contextId === clinicId),
      )
    : []

  return dedupeEventTypes([...matchedEventTypes, ...directEventTypes])
}

function getRoomEventTypes(roomData, clinicId, mentorEventTypes = []) {
  const helperEventTypes = getEventTypesFromRoomData(
    roomData?.checkInEventOptions,
    mentorEventTypes,
    clinicId,
  )
  const optionEventTypes = (roomData?.checkInEventOptions || []).flatMap((checkInOption) =>
    getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
  )

  return dedupeEventTypes([...helperEventTypes, ...optionEventTypes])
}

function getRoomServiceKeysFromCheckInOption(checkInOption, mentorEventTypes, clinicId) {
  return [
    ...new Set([
      ...getRoomServiceKeysFromEventTypes(
        getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
      ),
      ...getRoomServiceKeysFromOptionText(checkInOption),
    ]),
  ]
}

function resolveCheckInOptionForService(roomData, serviceKey, mentorEventTypes = [], clinicId) {
  const roomOptions = roomData?.checkInEventOptions || []

  return roomOptions
    .map((checkInOption, index) => {
      const roomServiceKeys = getRoomServiceKeysFromCheckInOption(
        checkInOption,
        mentorEventTypes,
        clinicId,
      )

      if (!roomServiceKeys.includes(serviceKey)) {
        return null
      }

      const matchedEventTypes = filterEventTypesForService(
        getEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
        serviceKey,
      )
      const optionSearchText = getCheckInOptionSearchText(checkInOption)
      const hasSingleService = roomServiceKeys.length === 1
      const hasPreferredKeyword =
        serviceKey === 'consultation'
          ? optionSearchText.includes('consult')
          : serviceKey === 'therapy'
          ? optionSearchText.includes('therapy') || optionSearchText.includes('assessment')
          : optionSearchText.includes('screen')

      return {
        checkInOption,
        matchedEventTypes,
        score:
          (hasSingleService ? 0 : 10) +
          (matchedEventTypes.length > 0 ? 0 : 3) +
          (hasPreferredKeyword ? 0 : 1) +
          index / 100,
      }
    })
    .filter(Boolean)
    .sort((optionA, optionB) => optionA.score - optionB.score)[0]
}

function getRoomServiceEntries(roomData, clinicId, mentorEventTypes = [], practitionerServices = []) {
  const clinicScopedMentorEventTypes = getEnabledClinicEventTypes(mentorEventTypes, clinicId)
  const roomEventTypes = getRoomEventTypes(roomData, clinicId, clinicScopedMentorEventTypes)
  const supportedRoomServiceKeys = new Set([
    ...getRoomServiceKeysFromEventTypes(roomEventTypes),
    ...(roomData?.checkInEventOptions || []).flatMap((checkInOption) =>
      getRoomServiceKeysFromCheckInOption(checkInOption, clinicScopedMentorEventTypes, clinicId),
    ),
  ])

  return practitionerServices
    .map((serviceConfig) => {
      const optionMatch = resolveCheckInOptionForService(
        roomData,
        serviceConfig.key,
        clinicScopedMentorEventTypes,
        clinicId,
      )
      const mentorServiceEventTypes = filterEventTypesForService(
        clinicScopedMentorEventTypes,
        serviceConfig.key,
      )

      if (!supportedRoomServiceKeys.has(serviceConfig.key) && !optionMatch) {
        return null
      }

      return {
        ...serviceConfig,
        checkInOption: optionMatch?.checkInOption || null,
        roomEventTypes:
          optionMatch?.matchedEventTypes?.length > 0
            ? optionMatch.matchedEventTypes
            : filterEventTypesForService(roomEventTypes, serviceConfig.key).length > 0
            ? filterEventTypesForService(roomEventTypes, serviceConfig.key)
            : mentorServiceEventTypes,
      }
    })
    .filter(Boolean)
}

function formatServiceLabels(serviceLabels = []) {
  if (serviceLabels.length === 0) return ''
  if (serviceLabels.length === 1) return serviceLabels[0]
  if (serviceLabels.length === 2) return `${serviceLabels[0]} and ${serviceLabels[1]}`

  return `${serviceLabels.slice(0, -1).join(', ')}, and ${serviceLabels[serviceLabels.length - 1]}`
}

function getAuditPractitionerName(roomData, isCurrentPractitionerRoom = false) {
  const practitionerName = roomData?.checkedInMentorName?.trim()

  if (practitionerName) {
    return practitionerName
  }

  return isCurrentPractitionerRoom ? 'Your Session' : 'Other Practitioner'
}

function getStoredCheckInOptionId(checkedInDetails = null) {
  return (
    checkedInDetails?.selectedCheckInEventOption ||
    checkedInDetails?.selectedCheckinEventOption ||
    null
  )
}

function getCheckInMethodOptions(roomData) {
  const checkInMethodOptions = Array.isArray(roomData?.checkInMethodOptions)
    ? roomData.checkInMethodOptions
    : []

  const normalizedMethodOptions =
    checkInMethodOptions.length > 0
      ? checkInMethodOptions
      : [
          {
            id: IN_PERSON_CHECKIN_METHOD,
            title: 'In Person',
            description: 'See patients physically at the clinic.',
            enabled: true,
          },
        ]

  const methodPriorityMap = {
    [IN_PERSON_CHECKIN_METHOD]: 0,
    [REMOTE_CHECKIN_METHOD]: 1,
  }

  return [...normalizedMethodOptions].sort((methodOptionA, methodOptionB) => {
    const priorityA = methodPriorityMap?.[methodOptionA?.id] ?? 99
    const priorityB = methodPriorityMap?.[methodOptionB?.id] ?? 99

    if (priorityA !== priorityB) return priorityA - priorityB
    return (methodOptionA?.title || '').localeCompare(methodOptionB?.title || '')
  })
}

function isCheckInMethodEnabled(methodOption) {
  return methodOption?.enabled !== false
}

function getDefaultCheckInMethod(
  methodOptions = [],
  preferredMethodId = null,
  preferRemoteByDefault = false,
) {
  const enabledMethodOptions = methodOptions.filter(isCheckInMethodEnabled)
  const candidateMethodOptions = enabledMethodOptions.length > 0 ? enabledMethodOptions : methodOptions

  if (preferredMethodId && candidateMethodOptions.some((methodOption) => methodOption?.id === preferredMethodId)) {
    return preferredMethodId
  }

  if (
    preferRemoteByDefault &&
    candidateMethodOptions.some((methodOption) => methodOption?.id === REMOTE_CHECKIN_METHOD)
  ) {
    return REMOTE_CHECKIN_METHOD
  }

  if (candidateMethodOptions.some((methodOption) => methodOption?.id === IN_PERSON_CHECKIN_METHOD)) {
    return IN_PERSON_CHECKIN_METHOD
  }

  return candidateMethodOptions?.[0]?.id || null
}

function getCheckInMethodDetails(methodOptions = [], methodId = null) {
  const matchedMethodOption = methodOptions.find((methodOption) => methodOption?.id === methodId)

  if (matchedMethodOption) {
    return matchedMethodOption
  }

  if (methodId === REMOTE_CHECKIN_METHOD) {
    return {
      id: REMOTE_CHECKIN_METHOD,
      title: 'Remote',
      description: 'Join the chamber room remotely.',
      enabled: true,
    }
  }

  return {
    id: IN_PERSON_CHECKIN_METHOD,
    title: 'In Person',
    description: 'See patients physically at the clinic.',
    enabled: true,
  }
}

function getRoomStatusFromScreenStatuses(chamberRoomId, screenStatuses) {
  if (!chamberRoomId || !Array.isArray(screenStatuses) || screenStatuses.length === 0) return 'unknown'
  const roomScreens = screenStatuses.filter((item) => item?.chamberRoomId === chamberRoomId)
  if (roomScreens.length === 0) return 'unknown'
  return roomScreens.some((item) => item?.status === 'up') ? 'online' : 'offline'
}

function getClinicLocationStatus(location) {
  const scheduleTimezone = location?.schedules?.[0]?.timezone
    ? decodeURIComponent(location?.schedules?.[0]?.timezone)
    : '+05:30'
  const weeklyHourMap = location?.schedules?.[0]?.weeklyHourMap

  if (!weeklyHourMap) {
    return {
      status: 'unknown',
      label: 'Schedule unavailable',
      accent: '#64748b',
    }
  }

  const currentDay = dayjs().format('dddd').toLowerCase()
  const todayTimings = weeklyHourMap?.[currentDay]

  if (!todayTimings?.available || !todayTimings?.slotList?.length) {
    return {
      status: 'closed',
      label: 'Closed today',
      accent: '#94a3b8',
    }
  }

  const now = dayjs().utcOffset(scheduleTimezone)
  let nextOpening = null

  for (const slot of todayTimings.slotList) {
    const [startHours, startMinutes] = slot?.startTime?.split(':') || [0, 0]
    const [endHours, endMinutes] = slot?.endTime?.split(':') || [0, 0]
    const todayStart = now.startOf('day')
    const slotStart = todayStart.hour(parseInt(startHours)).minute(parseInt(startMinutes)).second(0)
    const slotEnd = todayStart.hour(parseInt(endHours)).minute(parseInt(endMinutes)).second(0)

    if (now.isAfter(slotStart) && now.isBefore(slotEnd)) {
      const minutesUntilClose = slotEnd.diff(now, 'minute')
      if (minutesUntilClose <= 30) {
        return {
          status: 'closing-soon',
          label: `Closes in ${minutesUntilClose} min`,
          accent: '#d97706',
        }
      }

      return {
        status: 'open',
        label: `Open until ${slotEnd.format('h:mm A')}`,
        accent: '#059669',
      }
    }

    if (!nextOpening && slotStart.isAfter(now)) {
      nextOpening = slotStart
    }
  }

  if (nextOpening) {
    const minutesUntilOpen = nextOpening.diff(now, 'minute')
    if (minutesUntilOpen <= 60) {
      return {
        status: 'opening-soon',
        label: `Opens in ${minutesUntilOpen} min`,
        accent: '#2563eb',
      }
    }

    return {
      status: 'closed',
      label: `Opens at ${nextOpening.format('h:mm A')}`,
      accent: '#64748b',
    }
  }

  return {
    status: 'closed',
    label: 'Closed for today',
    accent: '#94a3b8',
  }
}

function BrandIcon({ brand }) {
  const brandImage = getImageLink(brand?.coverIcon || brand?.icon, brand?.coverImage, false)

  if (brandImage) {
    return (
      <Box
        component="img"
        src={brandImage}
        alt={brand?.title}
        sx={{
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          borderRadius: '18px',
          objectFit: 'cover',
          border: '1px solid rgba(124, 58, 237, 0.12)',
          boxShadow: '0 12px 24px rgba(124, 58, 237, 0.12)',
          backgroundColor: 'white',
          flexShrink: 0,
        }}
      />
    )
  }

  return (
    <Box
      sx={{
        width: { xs: 48, sm: 56 },
        height: { xs: 48, sm: 56 },
        borderRadius: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.16) 0%, rgba(14, 165, 233, 0.12) 100%)',
        border: '1px solid rgba(124, 58, 237, 0.12)',
        boxShadow: '0 12px 24px rgba(124, 58, 237, 0.08)',
        flexShrink: 0,
      }}
    >
      <LocalHospital sx={{ color: 'rgba(124, 58, 237, 0.9)', fontSize: { xs: 24, sm: 28 } }} />
    </Box>
  )
}

function RoomOperationsRow({
  roomData,
  clinicLocationData,
  checkedInDetails,
  onRefreshChamberStatuses,
  screenStatusesList,
  rosterAssignmentGroup = null,
}) {
  const navigate = useNavigate()
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [refreshing, setRefreshing] = useState(false)
  const [activeCheckInService, setActiveCheckInService] = useState(null)
  const [requestCheckoutDialogOpen, setRequestCheckoutDialogOpen] = useState(false)
  const [requestCheckoutLoading, setRequestCheckoutLoading] = useState(false)
  const checkInMethodOptions = useMemo(() => getCheckInMethodOptions(roomData), [roomData])
  const practitionerServices = useMemo(() => getPractitionerServicesFromAuthorities(), [])
  const preferRemoteByDefault = useMemo(
    () =>
      practitionerServices.some(
        (serviceConfig) =>
          serviceConfig?.key === 'therapy' || serviceConfig?.key === 'screening',
      ),
    [practitionerServices],
  )
  const roomServiceEntries = useMemo(
    () =>
      getRoomServiceEntries(
        roomData,
        clinicLocationData?.id,
        mentorProfileData?.eventTypes || [],
        practitionerServices,
      ),
    [clinicLocationData?.id, mentorProfileData?.eventTypes, practitionerServices, roomData],
  )

  const roomOccupied = roomData?.occupied
  const occupiedByThisMentor =
    roomData?.occupied && roomData?.checkedInMentorId === mentorProfileData?.id
  const isCurrentPractitionerRoom =
    occupiedByThisMentor ||
    (checkedInDetails?.chamberRoomId === roomData?.id &&
      checkedInDetails?.clinicLocationId === clinicLocationData?.id)
  const isRosterAssignedRoom = Boolean(rosterAssignmentGroup?.assignmentCount)
  const [selectedCheckInMethod, setSelectedCheckInMethod] = useState(() =>
    getDefaultCheckInMethod(checkInMethodOptions, null, preferRemoteByDefault),
  )
  const isCheckedInRoom =
    checkedInDetails?.chamberRoomId === roomData?.id &&
    checkedInDetails?.clinicLocationId === clinicLocationData?.id
  const roomScreenStatus = getRoomStatusFromScreenStatuses(roomData?.id, screenStatusesList)
  const storedCheckInOptionId = getStoredCheckInOptionId(checkedInDetails)
  const preferredRoomServiceEntry = useMemo(() => {
    if (!roomServiceEntries.length) return null

    const matchedStoredEntry = storedCheckInOptionId
      ? roomServiceEntries.find(
          (serviceEntry) => serviceEntry?.checkInOption?.id === storedCheckInOptionId,
        )
      : null

    if (matchedStoredEntry) {
      return matchedStoredEntry
    }

    if (practitionerServices.length === 1) {
      const matchedPractitionerEntry = roomServiceEntries.find(
        (serviceEntry) => serviceEntry?.key === practitionerServices?.[0]?.key,
      )

      if (matchedPractitionerEntry) {
        return matchedPractitionerEntry
      }
    }

    return roomServiceEntries.find((serviceEntry) => Boolean(serviceEntry?.checkInOption?.id)) || null
  }, [practitionerServices, roomServiceEntries, storedCheckInOptionId])
  const inferredCurrentRoomMethodId = useMemo(() => {
    if (!isCurrentPractitionerRoom) {
      return selectedCheckInMethod
    }

    const persistedMethodId = checkedInDetails?.checkinMethod || checkedInDetails?.checkInMethod
    if (persistedMethodId) {
      return persistedMethodId
    }

    const hasEnabledRemoteMethod = checkInMethodOptions.some(
      (methodOption) =>
        methodOption?.id === REMOTE_CHECKIN_METHOD && isCheckInMethodEnabled(methodOption),
    )

    if (hasEnabledRemoteMethod && roomData?.activeAuditChannelId) {
      return REMOTE_CHECKIN_METHOD
    }

    return getDefaultCheckInMethod(checkInMethodOptions, IN_PERSON_CHECKIN_METHOD, false)
  }, [
    checkInMethodOptions,
    checkedInDetails?.checkInMethod,
    checkedInDetails?.checkinMethod,
    isCurrentPractitionerRoom,
    roomData?.activeAuditChannelId,
    selectedCheckInMethod,
  ])
  const activeRoomMethodId = isCurrentPractitionerRoom
    ? inferredCurrentRoomMethodId
    : selectedCheckInMethod
  const activeMethodDetails = getCheckInMethodDetails(checkInMethodOptions, activeRoomMethodId)
  const canAuditAnotherPractitioner = Boolean(
    !isCurrentPractitionerRoom && roomData?.canVideoAudit && roomData?.activeAuditChannelId,
  )
  const supportedServiceLabel = roomServiceEntries.length
    ? `Supports ${formatServiceLabels(roomServiceEntries.map((serviceEntry) => serviceEntry.label))}.`
    : 'No consultation, therapy, or screening setup matching your role yet.'

  useEffect(() => {
    const preferredMethodId = isCurrentPractitionerRoom
      ? checkedInDetails?.checkinMethod || checkedInDetails?.checkInMethod
      : null

    setSelectedCheckInMethod((previousSelectedCheckInMethod) => {
      const validCurrentSelection = checkInMethodOptions.some(
        (methodOption) =>
          methodOption?.id === previousSelectedCheckInMethod && isCheckInMethodEnabled(methodOption),
      )

      if (preferredMethodId && preferredMethodId !== previousSelectedCheckInMethod) {
        return getDefaultCheckInMethod(
          checkInMethodOptions,
          preferredMethodId,
          preferRemoteByDefault,
        )
      }

      if (validCurrentSelection) {
        return previousSelectedCheckInMethod
      }

      return getDefaultCheckInMethod(
        checkInMethodOptions,
        preferredMethodId,
        preferRemoteByDefault,
      )
    })
  }, [
    checkInMethodOptions,
    checkedInDetails?.checkInMethod,
    checkedInDetails?.checkinMethod,
    isCurrentPractitionerRoom,
    preferRemoteByDefault,
  ])

  const tone = isCurrentPractitionerRoom
    ? {
        accent: '#7c3aed',
        background:
          'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(255, 255, 255, 0.96) 100%)',
        border: 'rgba(124, 58, 237, 0.26)',
      }
    : isRosterAssignedRoom
    ? {
        accent: '#0284c7',
        background:
          'linear-gradient(135deg, rgba(2, 132, 199, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)',
        border: 'rgba(2, 132, 199, 0.2)',
      }
    : roomOccupied
    ? {
        accent: '#e11d48',
        background:
          'linear-gradient(135deg, rgba(225, 29, 72, 0.08) 0%, rgba(255, 255, 255, 0.96) 100%)',
        border: 'rgba(225, 29, 72, 0.18)',
      }
    : {
        accent: '#0f766e',
        background:
          'linear-gradient(135deg, rgba(15, 118, 110, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)',
        border: 'rgba(15, 118, 110, 0.18)',
      }

  const availabilityLabel = isCurrentPractitionerRoom
    ? 'You are checked in'
    : isRosterAssignedRoom
    ? rosterAssignmentGroup?.status === 'current'
      ? 'Rostered now'
      : 'Rostered room'
    : roomOccupied
    ? 'Room busy'
    : 'Ready to check in'

  const rosterSummaryLabel = isRosterAssignedRoom
    ? `${
        rosterAssignmentGroup?.status === 'current'
          ? 'Rostered here right now'
          : rosterAssignmentGroup?.status === 'upcoming'
          ? 'Rostered here later today'
          : 'Rostered here today'
      }${rosterAssignmentGroup?.detailLabel ? `: ${rosterAssignmentGroup.detailLabel}.` : '.'}`
    : ''
  const secondaryLabel = roomOccupied
    ? isCurrentPractitionerRoom
      ? `${rosterSummaryLabel ? `${rosterSummaryLabel} ` : ''}${supportedServiceLabel} ${
          activeMethodDetails?.title || 'Current'
        } mode is active. Resume this chamber directly or check out when you are done.`
      : `${rosterSummaryLabel ? `${rosterSummaryLabel} ` : ''}${supportedServiceLabel} Present: ${
          roomData?.checkedInMentorName || 'Another mentor'
        }. Request a checkout to take over this chamber.`
    : `${rosterSummaryLabel ? `${rosterSummaryLabel} ` : ''}${supportedServiceLabel} Choose ${
        checkInMethodOptions.length > 1 ? 'a method and ' : ''
      }a service below to enter directly.`
  const auditPractitionerName = getAuditPractitionerName(roomData, isCurrentPractitionerRoom)
  const auditButtonLabel = `Video Audit ${auditPractitionerName}`

  const persistActiveRoomContext = (roomContext, serviceEntry, methodId) => {
    const effectiveRoomContext = roomContext || roomData
    const activeCheckInOptionId =
      getStoredCheckInOptionId(checkedInDetails) || serviceEntry?.checkInOption?.id || null

    const nextCheckInDetails = {
      ...(checkedInDetails || {}),
      clinicBrandId: clinicLocationData?.clinicBrandId,
      clinicLocationId: clinicLocationData?.id,
      chamberRoomId: roomData?.id,
      roomName:
        effectiveRoomContext?.title || effectiveRoomContext?.name || roomData?.title || 'Room unit',
      selectedCheckInEventOption: activeCheckInOptionId,
      checkinMethod: methodId,
      checkInMethod: methodId,
    }

    updateCheckInWithValidation(nextCheckInDetails)
    broadcastCheckInUpdate(nextCheckInDetails, effectiveRoomContext)

    localStorage.setItem('peekedInRoomDetails', JSON.stringify(effectiveRoomContext))

    const roomEventTypesToStore =
      serviceEntry?.roomEventTypes?.length > 0
        ? serviceEntry.roomEventTypes
        : dedupeEventTypes(
            roomServiceEntries.flatMap((serviceRow) => serviceRow?.roomEventTypes || []),
          )

    if (roomEventTypesToStore.length > 0) {
      localStorage.setItem('roomDataEventTypes', JSON.stringify(roomEventTypesToStore))
    } else {
      localStorage.removeItem('roomDataEventTypes')
    }

    return nextCheckInDetails
  }

  const navigateToCheckedInWorkspace = (checkInMethod = null) => {
    const resolvedMethodId = checkInMethod || activeRoomMethodId
    const nextRoute =
      resolvedMethodId === REMOTE_CHECKIN_METHOD
        ? `/mentor-home/join-chamber-room/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`
        : `/mentor-home/my-clinic-associations/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`

    navigate(nextRoute)
  }

  const handleOpenCheckedInWorkspace = async () => {
    const resumeStateKey = '__resume-current-room__'
    if (activeCheckInService) return

    const resumeMethodId = activeRoomMethodId
    const resumeServiceEntry = preferredRoomServiceEntry
    const resumeOptionId = getStoredCheckInOptionId(checkedInDetails) || resumeServiceEntry?.checkInOption?.id

    setActiveCheckInService(resumeStateKey)

    try {
      if (resumeOptionId) {
        const response = await refreshCheckIntoRoom({
          selectedCheckinEventOption: resumeOptionId,
          checkinMethod: resumeMethodId,
          clinicBrandId: clinicLocationData?.clinicBrandId,
          clinicLocationId: clinicLocationData?.id,
          chamberRoomId: roomData?.id,
        })

        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const emrUsageLog = response?.data?.data?.['emr-usage-log']
          const chamberRoom = response?.data?.data?.['chamber-room']

          if (emrUsageLog) {
            updateCheckInWithValidation({
              ...emrUsageLog,
              roomName: chamberRoom?.title || roomData?.title,
            })
            broadcastCheckInUpdate(emrUsageLog, chamberRoom || roomData)
          } else {
            persistActiveRoomContext(chamberRoom || roomData, resumeServiceEntry, resumeMethodId)
          }

          if (chamberRoom) {
            localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
          }

          const roomEventTypesToStore =
            resumeServiceEntry?.roomEventTypes?.length > 0
              ? resumeServiceEntry.roomEventTypes
              : dedupeEventTypes(
                  roomServiceEntries.flatMap((serviceRow) => serviceRow?.roomEventTypes || []),
                )

          if (roomEventTypesToStore.length > 0) {
            localStorage.setItem('roomDataEventTypes', JSON.stringify(roomEventTypesToStore))
          } else {
            localStorage.removeItem('roomDataEventTypes')
          }

          navigateToCheckedInWorkspace(resumeMethodId)
          return
        }
      }

      persistActiveRoomContext(roomData, resumeServiceEntry, resumeMethodId)
      navigateToCheckedInWorkspace(resumeMethodId)
    } catch (error) {
      console.error('Error reopening checked-in workspace:', error)
      persistActiveRoomContext(roomData, resumeServiceEntry, resumeMethodId)
      navigateToCheckedInWorkspace(resumeMethodId)
    } finally {
      setActiveCheckInService(null)
    }
  }

  const handleRefresh = async () => {
    if (!onRefreshChamberStatuses) return
    setRefreshing(true)
    try {
      await onRefreshChamberStatuses()
    } finally {
      setRefreshing(false)
    }
  }

  const handlePeekIn = async () => {
    navigate(
      `/mentor-home/my-clinic-associations/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`,
    )

    await peekIntoRoom({
      ...roomData,
      logEventId: 'enter-without-checkin',
    })

    localStorage.setItem('peekedInRoomDetails', JSON.stringify(roomData))
    const roomEventTypesToStore = dedupeEventTypes(
      roomServiceEntries.flatMap((serviceEntry) => serviceEntry.roomEventTypes || []),
    )
    if (roomEventTypesToStore.length > 0) {
      localStorage.setItem('roomDataEventTypes', JSON.stringify(roomEventTypesToStore))
    }
  }

  const handleDirectCheckIn = async (serviceEntry) => {
    if (!serviceEntry?.checkInOption?.id || activeCheckInService) return

    setActiveCheckInService(serviceEntry.key)
    try {
      const response = await checkIntoRoom({
        ...roomData,
        clinicBrandId: clinicLocationData?.clinicBrandId,
        clinicLocationId: clinicLocationData?.id,
        selectedCheckinEventOption: serviceEntry.checkInOption.id,
        checkinMethod: selectedCheckInMethod,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const emrUsageLog = response?.data?.data?.['emr-usage-log']
        const chamberRoom = response?.data?.data?.['chamber-room']
        const updatedCheckInDetails = emrUsageLog
          ? {
              ...emrUsageLog,
              roomName: chamberRoom?.title,
            }
          : null

        if (updatedCheckInDetails) {
          updateCheckInWithValidation(updatedCheckInDetails)
          broadcastCheckInUpdate(updatedCheckInDetails, chamberRoom)
        }

        if (chamberRoom) {
          localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
        }

        if (serviceEntry.roomEventTypes?.length > 0) {
          localStorage.setItem('roomDataEventTypes', JSON.stringify(serviceEntry.roomEventTypes))
        } else {
          localStorage.removeItem('roomDataEventTypes')
        }

        navigateToCheckedInWorkspace(selectedCheckInMethod)
      }
    } finally {
      setActiveCheckInService(null)
    }
  }

  const handleSubmitCheckoutRequest = async (reason) => {
    setRequestCheckoutLoading(true)

    try {
      const { request, response } = await createEmrCheckInRequest({
        chamberRoomId: roomData?.id,
        reason,
      })

      if (request) {
        setRequestCheckoutDialogOpen(false)
        toast.success(
          `Checkout request sent to ${roomData?.checkedInMentorName || 'the checked-in mentor'}.`,
        )
        return
      }

      toast.error(
        getCheckInRequestErrorMessage(response, 'Could not send the checkout request.'),
      )
    } catch (error) {
      console.error('Error sending checkout request:', error)
      toast.error('Could not send the checkout request.')
    } finally {
      setRequestCheckoutLoading(false)
    }
  }

  return (
    <Box
      sx={{
        borderRadius: '20px',
        border: `1px solid ${tone.border}`,
        background: tone.background,
        boxShadow: isCheckedInRoom
          ? '0 18px 32px rgba(124, 58, 237, 0.14)'
          : isRosterAssignedRoom
          ? '0 14px 28px rgba(2, 132, 199, 0.09)'
          : '0 10px 24px rgba(15, 23, 42, 0.06)',
        p: { xs: 1.5, sm: 2 },
        display: 'grid',
        gap: 1.5,
        alignItems: 'center',
        gridTemplateColumns: {
          xs: '1fr',
          lg: 'minmax(0, 1.6fr) minmax(320px, auto)',
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.25,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '16px', sm: '17px' },
                  fontWeight: 700,
                  color: 'rgba(15, 23, 42, 0.94)',
                }}
              >
                {roomData?.title || 'Room unit'}
              </Typography>
              <Chip
                size="small"
                label={availabilityLabel}
                sx={{
                  backgroundColor: alpha(tone.accent, 0.12),
                  color: tone.accent,
                  fontWeight: 700,
                  border: `1px solid ${alpha(tone.accent, 0.18)}`,
                }}
              />
              <Chip
                size="small"
                label={activeMethodDetails?.title || 'In Person'}
                sx={{
                  backgroundColor: 'rgba(14, 165, 233, 0.1)',
                  color: 'rgba(3, 105, 161, 0.94)',
                  fontWeight: 700,
                  border: '1px solid rgba(14, 165, 233, 0.16)',
                }}
              />
              {isRosterAssignedRoom && (
                <Chip
                  size="small"
                  label={
                    rosterAssignmentGroup?.status === 'current' ? 'Rostered now' : 'Rostered today'
                  }
                  sx={{
                    backgroundColor: 'rgba(2, 132, 199, 0.12)',
                    color: 'rgba(3, 105, 161, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(2, 132, 199, 0.16)',
                  }}
                />
              )}
              {isRosterAssignedRoom && Boolean(rosterAssignmentGroup?.detailLabel) && (
                <Chip
                  size="small"
                  label={rosterAssignmentGroup?.timeLabel || rosterAssignmentGroup?.detailLabel}
                  sx={{
                    backgroundColor: 'rgba(236, 254, 255, 0.88)',
                    color: 'rgba(14, 116, 144, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(125, 211, 252, 0.35)',
                  }}
                />
              )}
              {canAuditAnotherPractitioner && (
                <Chip
                  size="small"
                  label="Audit ready"
                  sx={{
                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                    color: 'rgba(37, 99, 235, 0.92)',
                    fontWeight: 700,
                    border: '1px solid rgba(37, 99, 235, 0.16)',
                  }}
                />
              )}
              {isCheckedInRoom && (
                <Chip
                  size="small"
                  label="Active room"
                  sx={{
                    backgroundColor: 'rgba(124, 58, 237, 0.92)',
                    color: 'white',
                    fontWeight: 700,
                  }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: 0.75,
                color: 'rgba(71, 85, 105, 0.88)',
                fontSize: { xs: '13px', sm: '14px' },
              }}
            >
              {secondaryLabel}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ChamberRoomStatusIndicator
              status={roomScreenStatus}
              chamberRoomName={roomData?.title}
              compact={true}
            />
            <Tooltip title="Refresh room status">
              <span>
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{
                    color: 'rgba(124, 58, 237, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(124, 58, 237, 0.08)',
                    },
                  }}
                >
                  <Refresh
                    sx={{
                      fontSize: 18,
                      animation: refreshing ? 'room-status-spin 0.8s linear infinite' : 'none',
                      '@keyframes room-status-spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'stretch', lg: 'flex-end' },
          gap: 1,
          minWidth: 0,
        }}
      >
        {!roomOccupied && checkInMethodOptions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'stretch', lg: 'flex-end' },
              gap: 0.75,
              width: '100%',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'rgba(71, 85, 105, 0.84)',
              }}
            >
              Check-in method
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: { xs: 'stretch', lg: 'flex-end' },
                gap: 0.75,
              }}
            >
              {checkInMethodOptions.map((methodOption) => {
                const isSelectedMethod = selectedCheckInMethod === methodOption?.id
                const methodEnabled = isCheckInMethodEnabled(methodOption)

                return (
                  <Button
                    key={methodOption?.id}
                    variant={isSelectedMethod ? 'contained' : 'outlined'}
                    color="emrSecondary"
                    size="small"
                    disabled={!methodEnabled || Boolean(activeCheckInService)}
                    onClick={() => setSelectedCheckInMethod(methodOption?.id)}
                    sx={{
                      borderRadius: '999px',
                      textTransform: 'none',
                      minHeight: 34,
                      px: 1.5,
                      flexGrow: { xs: 1, sm: 0 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {methodOption?.title}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: { xs: 'stretch', lg: 'flex-end' },
            gap: 1,
          }}
        >
          {!roomOccupied &&
            roomServiceEntries
              .filter((serviceEntry) => Boolean(serviceEntry?.checkInOption?.id))
              .map((serviceEntry) => {
                const loadingThisService = activeCheckInService === serviceEntry.key

                return (
                  <Button
                    key={serviceEntry.key}
                    variant="contained"
                    color="emrSecondary"
                    disabled={Boolean(activeCheckInService)}
                    onClick={() => handleDirectCheckIn(serviceEntry)}
                    startIcon={
                      loadingThisService ? <CircularProgress size={16} color="inherit" /> : null
                    }
                    sx={{
                      borderRadius: '999px',
                      textTransform: 'none',
                      minHeight: 40,
                      px: 2,
                      flexGrow: { xs: 1, sm: 0 },
                    }}
                  >
                    {loadingThisService ? `Opening ${serviceEntry.label}` : serviceEntry.label}
                  </Button>
                )
              })}

          {isCurrentPractitionerRoom && (
            <Button
              variant="contained"
              color="emrSecondary"
              disabled={Boolean(activeCheckInService)}
              onClick={handleOpenCheckedInWorkspace}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                minHeight: 40,
                px: 2,
                flexGrow: { xs: 1, sm: 0 },
              }}
            >
              {activeCheckInService === '__resume-current-room__'
                ? activeRoomMethodId === REMOTE_CHECKIN_METHOD
                  ? 'Joining Chamber...'
                  : 'Opening Chamber...'
                : activeRoomMethodId === REMOTE_CHECKIN_METHOD
                ? 'Join Chamber'
                : 'Open Chamber'}
            </Button>
          )}

          {!isCurrentPractitionerRoom && (
            <Button
              variant="outlined"
              color="emrSecondary"
              disabled={Boolean(activeCheckInService) || requestCheckoutLoading}
              onClick={handlePeekIn}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                minHeight: 40,
                px: 2,
                flexGrow: { xs: 1, sm: 0 },
              }}
            >
              Peek In / Calls
            </Button>
          )}

          {roomOccupied && !isCurrentPractitionerRoom && (
            <Button
              variant="contained"
              color="warning"
              disabled={Boolean(activeCheckInService) || requestCheckoutLoading}
              onClick={() => setRequestCheckoutDialogOpen(true)}
              startIcon={
                requestCheckoutLoading ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                minHeight: 40,
                px: 2,
                flexGrow: { xs: 1, sm: 0 },
              }}
            >
              {requestCheckoutLoading ? 'Sending request...' : 'Request Checkout'}
            </Button>
          )}

          {canAuditAnotherPractitioner && (
            <Button
              variant="outlined"
              color="emrSecondary"
              aria-label={auditButtonLabel}
              disabled={Boolean(activeCheckInService)}
              onClick={() => {
                localStorage.setItem('auditRoomDetails', JSON.stringify(roomData))
                navigate(
                  `/mentor-home/join-as-auditor/${roomData?.id}/${roomData?.activeAuditChannelId}`,
                )
              }}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                minHeight: 40,
                px: { xs: 1.5, sm: 2 },
                flexGrow: { xs: 1, sm: 0 },
                flexBasis: { xs: '100%', sm: 'auto' },
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'center' },
                minWidth: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 0.125, sm: 0.5 },
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: 'inherit',
                    fontWeight: { xs: 700, sm: 500 },
                    fontSize: { xs: '11px', sm: 'inherit' },
                    lineHeight: 1.1,
                    opacity: { xs: 0.82, sm: 1 },
                    flexShrink: 0,
                  }}
                >
                  Video Audit
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: 'inherit',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    minWidth: 0,
                    maxWidth: { xs: '100%', sm: '18ch', md: '24ch' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {auditPractitionerName}
                </Box>
              </Box>
            </Button>
          )}

          {isCurrentPractitionerRoom && (
            <Box sx={{ flexGrow: { xs: 1, sm: 0 } }}>
              <CheckoutButtonComponent
                styleProp={{
                  borderRadius: '999px',
                  minHeight: '40px',
                  padding: '0 20px',
                  width: '100%',
                }}
                buttonColor="error"
              />
            </Box>
          )}
        </Box>
      </Box>

      <CheckinRequestPromptDialog
        open={requestCheckoutDialogOpen}
        roomData={roomData}
        clinicLocationData={clinicLocationData}
        onClose={() => {
          if (!requestCheckoutLoading) {
            setRequestCheckoutDialogOpen(false)
          }
        }}
        onSubmit={handleSubmitCheckoutRequest}
        loading={requestCheckoutLoading}
      />
    </Box>
  )
}

function LocationOperationsSection({
  brand,
  location,
  locationCount = 1,
  waitingPatientsData,
  checkedInDetails,
  rosterAssignmentGroup = null,
  rosterAssignmentByRoomId = {},
  isClinicOwner,
  currentUserId,
  onNavigateToHotline,
  onOpenLocationStats,
  screenStatusesList,
  onRefreshChamberStatuses,
}) {
  const { mentorProfileData } = useContext(MentorProfileContext)

  const locationKey = `${brand?.id}-${location?.id}`
  const locationMetrics = waitingPatientsData?.[locationKey] || {}
  const locationStatus = useMemo(() => getClinicLocationStatus(location), [location])
  const canViewStats = isClinicOwner && brand?.ownerUserIds?.includes(currentUserId)
  const isCheckedInLocation =
    checkedInDetails?.clinicLocationId === location?.id &&
    checkedInDetails?.clinicBrandId === brand?.id
  const waitingCount = locationMetrics?.count || 0
  const bookingCount = locationMetrics?.totalBookings || 0
  const isRosterAssignedLocation = Boolean(rosterAssignmentGroup?.assignmentCount)
  const shouldAutoExpand =
    locationCount === 1 ||
    isCheckedInLocation ||
    isRosterAssignedLocation ||
    waitingCount > 0 ||
    bookingCount > 0
  const [isExpanded, setIsExpanded] = useState(shouldAutoExpand)

  const rooms = useMemo(
    () =>
      [...(location?.rooms || [])].sort((roomA, roomB) => {
        const roomRank = (roomItem) => {
          const roomRosterAssignment = rosterAssignmentByRoomId?.[roomItem?.id]
          const isActiveCheckedInRoom =
            checkedInDetails?.chamberRoomId === roomItem?.id &&
            checkedInDetails?.clinicLocationId === location?.id
          const occupiedByMe =
            roomItem?.occupied && roomItem?.checkedInMentorId === mentorProfileData?.id
          if (isActiveCheckedInRoom || occupiedByMe) return 0
          if (roomRosterAssignment?.status === 'current') return 1
          if (roomRosterAssignment?.status === 'upcoming') return 2
          if (!roomItem?.occupied) return 3
          if (roomItem?.canVideoAudit && roomItem?.activeAuditChannelId) return 4
          return 5
        }

        const rankDifference = roomRank(roomA) - roomRank(roomB)
        if (rankDifference !== 0) return rankDifference
        return (roomA?.title || '').localeCompare(roomB?.title || '')
      }),
    [
      checkedInDetails?.chamberRoomId,
      checkedInDetails?.clinicLocationId,
      location?.id,
      location?.rooms,
      mentorProfileData?.id,
      rosterAssignmentByRoomId,
    ],
  )

  useEffect(() => {
    if (shouldAutoExpand) {
      setIsExpanded(true)
    }
  }, [shouldAutoExpand])

  const totalRooms = rooms.length
  const availableRooms = rooms.filter((roomItem) => !roomItem?.occupied).length
  const auditReadyRooms = rooms.filter(
    (roomItem) => roomItem?.canVideoAudit && roomItem?.activeAuditChannelId,
  ).length
  const hasSecondaryActions = Boolean(location?.hasHotline) || canViewStats

  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: isCheckedInLocation
          ? '1px solid rgba(124, 58, 237, 0.26)'
          : isRosterAssignedLocation
          ? '1px solid rgba(2, 132, 199, 0.22)'
          : '1px solid rgba(148, 163, 184, 0.2)',
        background: isCheckedInLocation
          ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
          : isRosterAssignedLocation
          ? 'linear-gradient(135deg, rgba(2, 132, 199, 0.06) 0%, rgba(255, 255, 255, 0.98) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
        boxShadow: isCheckedInLocation
          ? '0 22px 40px rgba(124, 58, 237, 0.08)'
          : isRosterAssignedLocation
          ? '0 18px 34px rgba(2, 132, 199, 0.06)'
          : '0 16px 30px rgba(15, 23, 42, 0.05)',
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={() => setIsExpanded((previousExpanded) => !previousExpanded)}
        sx={{
          width: '100%',
          textAlign: 'left',
          display: 'block',
          '&:hover': {
            backgroundColor: alpha(isCheckedInLocation ? '#7c3aed' : '#0f172a', 0.02),
          },
        }}
      >
        <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '18px', sm: '20px' },
                    fontWeight: 700,
                    color: 'rgba(15, 23, 42, 0.95)',
                  }}
                >
                  {location?.name}
                </Typography>
                <Chip
                  size="small"
                  label={locationStatus.label}
                  sx={{
                    backgroundColor: alpha(locationStatus.accent, 0.12),
                    color: locationStatus.accent,
                    fontWeight: 700,
                    border: `1px solid ${alpha(locationStatus.accent, 0.18)}`,
                  }}
                />
                {isCheckedInLocation && (
                  <Chip
                    size="small"
                    label="Current clinic"
                    sx={{
                      backgroundColor: 'rgba(124, 58, 237, 0.92)',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                )}
                {isRosterAssignedLocation && (
                  <Chip
                    size="small"
                    label={
                      rosterAssignmentGroup?.status === 'current'
                        ? 'Rostered here now'
                        : 'Rostered here today'
                    }
                    sx={{
                      backgroundColor: 'rgba(2, 132, 199, 0.12)',
                      color: 'rgba(3, 105, 161, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(2, 132, 199, 0.16)',
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  color: 'rgba(71, 85, 105, 0.85)',
                }}
              >
                Room actions stay grouped right here so you can move top-down from location to room.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-end',
                ml: 'auto',
              }}
            >
              <Chip
                size="small"
                icon={<MeetingRoom sx={{ fontSize: '16px !important' }} />}
                label={`${availableRooms}/${totalRooms} ready`}
                sx={{
                  backgroundColor: 'rgba(15, 118, 110, 0.1)',
                  color: 'rgba(15, 118, 110, 0.92)',
                  fontWeight: 700,
                  border: '1px solid rgba(15, 118, 110, 0.14)',
                }}
              />
              {Boolean(waitingCount) && (
                <Chip
                  size="small"
                  icon={<People sx={{ fontSize: '16px !important' }} />}
                  label={`${waitingCount} waiting`}
                  sx={{
                    backgroundColor: 'rgba(124, 58, 237, 0.1)',
                    color: 'rgba(124, 58, 237, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(124, 58, 237, 0.14)',
                  }}
                />
              )}
              {Boolean(bookingCount) && (
                <Chip
                  size="small"
                  label={`${bookingCount} bookings`}
                  sx={{
                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                    color: 'rgba(37, 99, 235, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(37, 99, 235, 0.14)',
                  }}
                />
              )}
              {isRosterAssignedLocation && Boolean(rosterAssignmentGroup?.detailLabel) && (
                <Chip
                  size="small"
                  icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                  label={rosterAssignmentGroup.detailLabel}
                  sx={{
                    backgroundColor: 'rgba(236, 254, 255, 0.88)',
                    color: 'rgba(14, 116, 144, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(125, 211, 252, 0.35)',
                  }}
                />
              )}
              {Boolean(locationMetrics?.earliestTime) && (
                <Chip
                  size="small"
                  icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                  label={`Next ${dayjs.utc(locationMetrics.earliestTime).local().format('h:mm A')}`}
                  sx={{
                    backgroundColor: 'rgba(249, 115, 22, 0.1)',
                    color: 'rgba(194, 65, 12, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(249, 115, 22, 0.14)',
                  }}
                />
              )}
              {auditReadyRooms > 0 && (
                <Chip
                  size="small"
                  label={`${auditReadyRooms} audit ready`}
                  sx={{
                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                    color: 'rgba(8, 145, 178, 0.94)',
                    fontWeight: 700,
                    border: '1px solid rgba(6, 182, 212, 0.14)',
                  }}
                />
              )}
              <Chip
                size="small"
                label={isExpanded ? 'Collapse' : 'Expand'}
                sx={{
                  backgroundColor: alpha(isCheckedInLocation ? '#7c3aed' : '#0f172a', 0.06),
                  color: isCheckedInLocation ? 'rgba(124, 58, 237, 0.96)' : 'rgba(15, 23, 42, 0.82)',
                  fontWeight: 700,
                  border: `1px solid ${alpha(isCheckedInLocation ? '#7c3aed' : '#64748b', 0.14)}`,
                }}
              />
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '999px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isCheckedInLocation ? 'rgba(124, 58, 237, 0.96)' : 'rgba(51, 65, 85, 0.82)',
                  backgroundColor: alpha(isCheckedInLocation ? '#7c3aed' : '#e2e8f0', 0.1),
                  border: `1px solid ${alpha(isCheckedInLocation ? '#7c3aed' : '#64748b', 0.12)}`,
                  flexShrink: 0,
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </Box>
            </Box>
          </Box>
        </Box>
      </ButtonBase>

      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ px: { xs: 1.75, sm: 2.25 }, pb: { xs: 1.75, sm: 2.25 } }}>
          {hasSecondaryActions && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {Boolean(location?.hasHotline) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onNavigateToHotline?.(location)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    minHeight: 36,
                  }}
                >
                  Join Hotline
                </Button>
              )}
              {canViewStats && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onOpenLocationStats?.(location)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    minHeight: 36,
                  }}
                >
                  View Stats
                </Button>
              )}
            </Box>
          )}

          <Divider
            sx={{
              my: hasSecondaryActions ? 2 : 0,
              mb: 2,
              borderColor: 'rgba(148, 163, 184, 0.16)',
            }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {rooms.length > 0 ? (
              rooms.map((roomItem) => (
                <RoomOperationsRow
                  key={roomItem?.id}
                  roomData={roomItem}
                  clinicLocationData={location}
                  checkedInDetails={checkedInDetails}
                  onRefreshChamberStatuses={onRefreshChamberStatuses}
                  screenStatusesList={screenStatusesList}
                  rosterAssignmentGroup={rosterAssignmentByRoomId?.[roomItem?.id] || null}
                />
              ))
            ) : (
              <Box
                sx={{
                  borderRadius: '18px',
                  border: '1px dashed rgba(148, 163, 184, 0.34)',
                  backgroundColor: 'rgba(248, 250, 252, 0.9)',
                  px: 2,
                  py: 2.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(71, 85, 105, 0.88)',
                  }}
                >
                  No room units are linked to this location yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

export default function MentorClinicAssociationsListView({
  brands = [],
  waitingPatientsData = {},
  checkedInDetails = null,
  isClinicOwner = false,
  currentUserId = null,
  onNavigateToHotline,
  onOpenLocationStats,
  screenStatusesList = [],
  onRefreshChamberStatuses,
  rosterAssignmentIndex = EMPTY_ROSTER_ASSIGNMENT_INDEX,
}) {
  const [expandedBrandIds, setExpandedBrandIds] = useState([])
  const rosterAssignmentsByBrandId = rosterAssignmentIndex.byBrandId
  const rosterAssignmentsByLocationKey = rosterAssignmentIndex.byLocationKey
  const rosterAssignmentsByRoomId = rosterAssignmentIndex.byRoomId
  const orderedBrands = useMemo(() => {
    const brandStatusPriority = {
      current: 1,
      upcoming: 2,
      completed: 3,
    }

    return brands
      .map((brand, index) => ({
        brand,
        index,
        isCheckedInBrand: checkedInDetails?.clinicBrandId === brand?.id,
        rosterAssignmentGroup: rosterAssignmentsByBrandId?.[brand?.id] || null,
      }))
      .sort((leftBrand, rightBrand) => {
        if (leftBrand.isCheckedInBrand !== rightBrand.isCheckedInBrand) {
          return leftBrand.isCheckedInBrand ? -1 : 1
        }

        const leftRosterPriority = leftBrand.rosterAssignmentGroup
          ? brandStatusPriority?.[leftBrand.rosterAssignmentGroup.status] ?? 99
          : 99
        const rightRosterPriority = rightBrand.rosterAssignmentGroup
          ? brandStatusPriority?.[rightBrand.rosterAssignmentGroup.status] ?? 99
          : 99

        if (leftRosterPriority !== rightRosterPriority) {
          return leftRosterPriority - rightRosterPriority
        }

        return leftBrand.index - rightBrand.index
      })
      .map(({ brand }) => brand)
  }, [brands, checkedInDetails?.clinicBrandId, rosterAssignmentsByBrandId])

  const totals = useMemo(() => {
    const allLocations = brands.flatMap((brand) => brand?.locations || [])
    const allRooms = allLocations.flatMap((location) => location?.rooms || [])

    return {
      brandCount: brands.length,
      locationCount: allLocations.length,
      roomCount: allRooms.length,
      waitingCount: allLocations.reduce((total, location) => {
        const key = `${location?.clinicBrandId}-${location?.id}`
        return total + (waitingPatientsData?.[key]?.count || 0)
      }, 0),
    }
  }, [brands, waitingPatientsData])

  const autoExpandedBrandIds = useMemo(
    () =>
      orderedBrands
        .filter((brand) =>
          Boolean(rosterAssignmentsByBrandId?.[brand?.id]?.assignmentCount) ||
          (brand?.locations || []).some((location) => {
            const locationMetrics = waitingPatientsData?.[`${brand?.id}-${location?.id}`]
            return (locationMetrics?.count || 0) > 0 || (locationMetrics?.totalBookings || 0) > 0
          }),
        )
        .map((brand) => brand?.id)
        .filter(Boolean),
    [orderedBrands, rosterAssignmentsByBrandId, waitingPatientsData],
  )

  useEffect(() => {
    const availableBrandIds = orderedBrands.map((brand) => brand?.id).filter(Boolean)

    setExpandedBrandIds((previousExpandedBrandIds) => {
      const filteredExpandedBrandIds = previousExpandedBrandIds.filter((brandId) =>
        availableBrandIds.includes(brandId),
      )

      if (orderedBrands.length === 1) {
        return orderedBrands?.[0]?.id ? [orderedBrands[0].id] : []
      }

      if (
        checkedInDetails?.clinicBrandId &&
        availableBrandIds.includes(checkedInDetails.clinicBrandId)
      ) {
        return [
          ...new Set([
            checkedInDetails.clinicBrandId,
            ...autoExpandedBrandIds,
            ...filteredExpandedBrandIds,
          ]),
        ]
      }

      if (autoExpandedBrandIds.length > 0) {
        return [...new Set([...autoExpandedBrandIds, ...filteredExpandedBrandIds])]
      }

      return filteredExpandedBrandIds
    })
  }, [autoExpandedBrandIds, orderedBrands, checkedInDetails?.clinicBrandId])

  const toggleBrandExpansion = (brandId) => {
    if (!brandId) return

    setExpandedBrandIds((previousExpandedBrandIds) =>
      previousExpandedBrandIds.includes(brandId)
        ? previousExpandedBrandIds.filter((expandedBrandId) => expandedBrandId !== brandId)
        : [...previousExpandedBrandIds, brandId],
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 3 } }}>
      <Box
        sx={{
          borderRadius: '30px',
          border: '1px solid rgba(124, 58, 237, 0.14)',
          background:
            'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(14, 165, 233, 0.08) 36%, rgba(255, 255, 255, 0.98) 100%)',
          boxShadow: '0 28px 54px rgba(15, 23, 42, 0.08)',
          px: { xs: 2, sm: 2.5, md: 3 },
          py: { xs: 2, sm: 2.25, md: 2.75 },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '22px', sm: '26px' },
            fontWeight: 800,
            color: 'rgba(15, 23, 42, 0.96)',
          }}
        >
          Clinic operations, now in a clean working list
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 1,
            maxWidth: '760px',
            color: 'rgba(51, 65, 85, 0.84)',
            lineHeight: 1.6,
          }}
        >
          Move top-down from clinic brand to location to room unit, with check-in, peek-in, and
          audit actions kept in the same flow.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Chip
            label={`${totals.brandCount} brand${totals.brandCount !== 1 ? 's' : ''}`}
            sx={{
              backgroundColor: 'rgba(124, 58, 237, 0.12)',
              color: 'rgba(124, 58, 237, 0.94)',
              fontWeight: 700,
              border: '1px solid rgba(124, 58, 237, 0.14)',
            }}
          />
          <Chip
            label={`${totals.locationCount} location${totals.locationCount !== 1 ? 's' : ''}`}
            sx={{
              backgroundColor: 'rgba(14, 165, 233, 0.12)',
              color: 'rgba(3, 105, 161, 0.94)',
              fontWeight: 700,
              border: '1px solid rgba(14, 165, 233, 0.14)',
            }}
          />
          <Chip
            label={`${totals.roomCount} room unit${totals.roomCount !== 1 ? 's' : ''}`}
            sx={{
              backgroundColor: 'rgba(15, 118, 110, 0.12)',
              color: 'rgba(15, 118, 110, 0.94)',
              fontWeight: 700,
              border: '1px solid rgba(15, 118, 110, 0.14)',
            }}
          />
          {totals.waitingCount > 0 && (
            <Chip
              label={`${totals.waitingCount} patient${totals.waitingCount !== 1 ? 's' : ''} waiting`}
              sx={{
                backgroundColor: 'rgba(249, 115, 22, 0.12)',
                color: 'rgba(194, 65, 12, 0.94)',
                fontWeight: 700,
                border: '1px solid rgba(249, 115, 22, 0.14)',
              }}
            />
          )}
          {rosterAssignmentIndex?.summary?.totalRooms > 0 && (
            <Chip
              icon={<MeetingRoom sx={{ fontSize: '16px !important' }} />}
              label={`${rosterAssignmentIndex.summary.totalRooms} rostered room${
                rosterAssignmentIndex.summary.totalRooms !== 1 ? 's' : ''
              } today`}
              sx={{
                backgroundColor: 'rgba(2, 132, 199, 0.12)',
                color: 'rgba(3, 105, 161, 0.94)',
                fontWeight: 700,
                border: '1px solid rgba(2, 132, 199, 0.16)',
              }}
            />
          )}
        </Box>
      </Box>

      {orderedBrands.map((brand) => {
        const totalRooms = (brand?.locations || []).reduce(
          (total, location) => total + (location?.rooms || []).length,
          0,
        )
        const totalWaiting = (brand?.locations || []).reduce((total, location) => {
          const key = `${brand?.id}-${location?.id}`
          return total + (waitingPatientsData?.[key]?.count || 0)
        }, 0)
        const earliestTime = (brand?.locations || []).reduce((earliest, location) => {
          const key = `${brand?.id}-${location?.id}`
          const nextTime = waitingPatientsData?.[key]?.earliestTime
          if (!nextTime) return earliest
          if (!earliest || dayjs(nextTime).isBefore(dayjs(earliest))) {
            return nextTime
          }
          return earliest
        }, null)
        const isActiveBrand = checkedInDetails?.clinicBrandId === brand?.id
        const brandRosterAssignmentGroup = rosterAssignmentsByBrandId?.[brand?.id] || null
        const isRosterAssignedBrand = Boolean(brandRosterAssignmentGroup?.assignmentCount)
        const isBrandExpanded = expandedBrandIds.includes(brand?.id)
        const orderedLocations = [...(brand?.locations || [])]
          .map((location, index) => ({
            location,
            index,
            isCheckedInLocation:
              checkedInDetails?.clinicLocationId === location?.id &&
              checkedInDetails?.clinicBrandId === brand?.id,
            rosterAssignmentGroup:
              rosterAssignmentsByLocationKey?.[`${brand?.id}-${location?.id}`] || null,
          }))
          .sort((leftLocation, rightLocation) => {
            const locationStatusPriority = {
              current: 1,
              upcoming: 2,
              completed: 3,
            }

            if (leftLocation.isCheckedInLocation !== rightLocation.isCheckedInLocation) {
              return leftLocation.isCheckedInLocation ? -1 : 1
            }

            const leftRosterPriority = leftLocation.rosterAssignmentGroup
              ? locationStatusPriority?.[leftLocation.rosterAssignmentGroup.status] ?? 99
              : 99
            const rightRosterPriority = rightLocation.rosterAssignmentGroup
              ? locationStatusPriority?.[rightLocation.rosterAssignmentGroup.status] ?? 99
              : 99

            if (leftRosterPriority !== rightRosterPriority) {
              return leftRosterPriority - rightRosterPriority
            }

            return leftLocation.index - rightLocation.index
          })
          .map(({ location }) => location)

        return (
          <Box
            key={brand?.id}
            sx={{
              borderRadius: '30px',
              border: isActiveBrand
                ? '1px solid rgba(124, 58, 237, 0.26)'
                : isRosterAssignedBrand
                ? '1px solid rgba(2, 132, 199, 0.18)'
                : '1px solid rgba(148, 163, 184, 0.16)',
              background: isActiveBrand
                ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                : isRosterAssignedBrand
                ? 'linear-gradient(135deg, rgba(2, 132, 199, 0.05) 0%, rgba(255, 255, 255, 0.98) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.96) 100%)',
              boxShadow: isActiveBrand
                ? '0 28px 48px rgba(124, 58, 237, 0.12)'
                : isRosterAssignedBrand
                ? '0 22px 42px rgba(2, 132, 199, 0.05)'
                : '0 22px 42px rgba(15, 23, 42, 0.06)',
              overflow: 'hidden',
            }}
          >
            <ButtonBase
              onClick={() => toggleBrandExpansion(brand?.id)}
              sx={{
                width: '100%',
                textAlign: 'left',
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 2, sm: 2.5, md: 3 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                flexWrap: 'wrap',
                '&:hover': {
                  backgroundColor: alpha(isActiveBrand ? '#7c3aed' : '#0f172a', 0.02),
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <BrandIcon brand={brand} />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: { xs: '22px', sm: '24px', md: '28px' },
                        fontWeight: 800,
                        color: 'rgba(15, 23, 42, 0.96)',
                      }}
                    >
                      {brand?.title}
                    </Typography>
                    {isActiveBrand && (
                      <Chip
                        size="small"
                        label="Checked in here"
                        sx={{
                          backgroundColor: 'rgba(124, 58, 237, 0.92)',
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {isRosterAssignedBrand && (
                      <Chip
                        size="small"
                        label={
                          brandRosterAssignmentGroup?.status === 'current'
                            ? 'Rostered here now'
                            : 'Rostered here today'
                        }
                        sx={{
                          backgroundColor: 'rgba(2, 132, 199, 0.12)',
                          color: 'rgba(3, 105, 161, 0.94)',
                          fontWeight: 700,
                          border: '1px solid rgba(2, 132, 199, 0.16)',
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      color: 'rgba(71, 85, 105, 0.84)',
                    }}
                  >
                    {brand?.locations?.length || 0} location
                    {(brand?.locations?.length || 0) !== 1 ? 's' : ''} and {totalRooms} room unit
                    {totalRooms !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  ml: 'auto',
                }}
              >
                {totalWaiting > 0 && (
                  <Chip
                    size="small"
                    icon={<People sx={{ fontSize: '16px !important' }} />}
                    label={`${totalWaiting} waiting`}
                    sx={{
                      backgroundColor: 'rgba(124, 58, 237, 0.12)',
                      color: 'rgba(124, 58, 237, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(124, 58, 237, 0.14)',
                    }}
                  />
                )}
                {isRosterAssignedBrand && Boolean(brandRosterAssignmentGroup?.detailLabel) && (
                  <Chip
                    size="small"
                    icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                    label={brandRosterAssignmentGroup.detailLabel}
                    sx={{
                      backgroundColor: 'rgba(236, 254, 255, 0.88)',
                      color: 'rgba(14, 116, 144, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(125, 211, 252, 0.35)',
                    }}
                  />
                )}
                {earliestTime && (
                  <Chip
                    size="small"
                    icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                    label={`Next ${dayjs.utc(earliestTime).local().format('h:mm A')}`}
                    sx={{
                      backgroundColor: 'rgba(249, 115, 22, 0.12)',
                      color: 'rgba(194, 65, 12, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(249, 115, 22, 0.14)',
                    }}
                  />
                )}
                <Chip
                  size="small"
                  label={isBrandExpanded ? 'Collapse' : 'Expand'}
                  sx={{
                    backgroundColor: alpha(isActiveBrand ? '#7c3aed' : '#0f172a', 0.06),
                    color: isActiveBrand ? 'rgba(124, 58, 237, 0.96)' : 'rgba(15, 23, 42, 0.82)',
                    fontWeight: 700,
                    border: `1px solid ${alpha(isActiveBrand ? '#7c3aed' : '#64748b', 0.14)}`,
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '999px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActiveBrand ? 'rgba(124, 58, 237, 0.96)' : 'rgba(51, 65, 85, 0.82)',
                    backgroundColor: alpha(isActiveBrand ? '#7c3aed' : '#e2e8f0', 0.1),
                    border: `1px solid ${alpha(isActiveBrand ? '#7c3aed' : '#64748b', 0.12)}`,
                    flexShrink: 0,
                  }}
                >
                  {isBrandExpanded ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </Box>
            </ButtonBase>

            <Collapse in={isBrandExpanded} timeout="auto">
              <Box
                sx={{
                  px: { xs: 2, sm: 2.5, md: 3 },
                  pb: { xs: 2, sm: 2.5, md: 3 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {orderedLocations.map((location) => (
                  <LocationOperationsSection
                    key={location?.id}
                    brand={brand}
                    location={location}
                    locationCount={brand?.locations?.length || 0}
                    waitingPatientsData={waitingPatientsData}
                    checkedInDetails={checkedInDetails}
                    rosterAssignmentGroup={
                      rosterAssignmentsByLocationKey?.[`${brand?.id}-${location?.id}`] || null
                    }
                    rosterAssignmentByRoomId={rosterAssignmentsByRoomId}
                    isClinicOwner={isClinicOwner}
                    currentUserId={currentUserId}
                    onNavigateToHotline={onNavigateToHotline}
                    onOpenLocationStats={onOpenLocationStats}
                    screenStatusesList={screenStatusesList}
                    onRefreshChamberStatuses={onRefreshChamberStatuses}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )
      })}
    </Box>
  )
}
